import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Label } from "@/components/ui/Input";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { hubModalBodyScrollClassName, hubModalShellClassName } from "@/lib/hubLayoutClassNames";

import {
  previewInsideSalesPendingPortalUserOrganization,
  searchInsideSalesWirelessPortalOrganizations,
  type PendingPortalJoinPerson,
  type PendingPortalUserItem,
} from "./insideSalesWirelessPortalAccountsApi";
import { DEFAULT_US_TIME_ZONE, US_TIME_ZONE_OPTIONS } from "./insideSalesWirelessPortalTimeZones";

function matchingOrganizationId(
  user: PendingPortalUserItem,
  organizations: Array<{ organizationId: number; name: string }>,
): string {
  const requested = user.organizationName.trim().toLowerCase();
  if (requested === "") return "";
  const matches = organizations.filter((organization) => organization.name.trim().toLowerCase() === requested);
  return matches.length === 1 ? String(matches[0].organizationId) : "";
}

function PersonRow(props: {
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  role: string;
  issues: PendingPortalJoinPerson | undefined;
}) {
  const errors = props.issues?.errors ?? [];
  const warnings = props.issues?.warnings ?? [];
  const borderClass = errors.length > 0
    ? "border-rose-200 bg-rose-50/40"
    : warnings.length > 0
      ? "border-amber-200 bg-amber-50/40"
      : "border-slate-200 bg-white";
  return (
    <div className={`rounded-md border px-3 py-2 ${borderClass}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-slate-900">
          {props.firstName} {props.lastName}
        </p>
        <span className="text-xs font-medium text-slate-600">{props.role}</span>
      </div>
      <p className="mt-0.5 truncate text-sm text-slate-700" title={props.email}>
        {props.email}
      </p>
      {props.phone ? <p className="text-xs text-slate-500">{props.phone}</p> : null}
      {errors.map((message) => (
        <p key={message} className="mt-1 text-xs text-rose-700">
          {message}
        </p>
      ))}
      {warnings.map((message) => (
        <p key={message} className="mt-1 text-xs text-amber-800">
          {message}
        </p>
      ))}
      <p className="sr-only">{props.label}</p>
    </div>
  );
}

export function ApprovePendingPortalUserModal(props: {
  user: PendingPortalUserItem | null;
  pending: boolean;
  denyPending: boolean;
  onClose: () => void;
  onApprove: (input: { organizationId: number; organizationName: string; timeZone: string }) => void;
  onDeny: () => void;
}) {
  const { user, pending, denyPending, onClose, onApprove, onDeny } = props;
  const readOnly = user != null && user.status !== "Pending";
  const [organizationId, setOrganizationId] = useState("");
  const [organizationSearch, setOrganizationSearch] = useState("");
  const [timeZone, setTimeZone] = useState(DEFAULT_US_TIME_ZONE);
  const [timeZoneSearch, setTimeZoneSearch] = useState("");
  const busy = pending || denyPending;

  const organizationsQuery = useQuery({
    queryKey: ["inside-sales", "wireless-portal-organizations", "all"],
    queryFn: () => searchInsideSalesWirelessPortalOrganizations(""),
    enabled: user != null,
  });

  const organizations = organizationsQuery.data?.organizations ?? [];
  const selected = useMemo(
    () => organizations.find((organization) => String(organization.organizationId) === organizationId) ?? null,
    [organizationId, organizations],
  );
  const emails = user == null ? [] : [user.adminEmail, ...user.additionalUsers.map((entry) => entry.email)];
  const membershipQuery = useQuery({
    queryKey: ["inside-sales", "wireless-portal-pending-review", organizationId, emails.join(",")],
    queryFn: () =>
      previewInsideSalesPendingPortalUserOrganization(
        emails,
        organizationId === "" ? undefined : Number(organizationId),
      ),
    enabled: user != null && emails.length > 0,
  });

  useEffect(() => {
    if (user == null) return;
    setOrganizationSearch(user.organizationName);
    setOrganizationId(matchingOrganizationId(user, organizations));
    setTimeZone(DEFAULT_US_TIME_ZONE);
    setTimeZoneSearch("");
  }, [user, organizations]);

  if (user == null) return null;

  const people = membershipQuery.data?.people ?? [];
  const canSubmit =
    !readOnly &&
    selected != null &&
    timeZone !== "" &&
    membershipQuery.data?.ok === true &&
    !membershipQuery.isFetching &&
    !busy;
  const requestedOrg = user.organizationName.trim();
  const selectedDiffersFromRequested =
    selected != null && requestedOrg !== "" && selected.name.trim().toLowerCase() !== requestedOrg.toLowerCase();
  const skippedExistingCount = people.filter((person) => person.alreadyInPortal).length;
  const creatableCount = membershipQuery.data?.creatableEmails.length ?? 0;

  return createPortal(
    <div {...hubModalOverlayProps(true)}>
      <div
        className={hubModalShellClassName("form")}
        role="dialog"
        aria-modal="true"
        aria-labelledby="approve-pending-portal-user-title"
      >
        <div className="flex shrink-0 items-center gap-2 border-b border-slate-200 px-4 py-3">
          <h2 id="approve-pending-portal-user-title" className="text-base font-semibold text-slate-900">
            {readOnly ? `${user.status} portal request` : "Approve portal request"}
          </h2>
          <HoverHintWithGlyph hoverHint="Match the typed organization to a portal org, then approve. People already in the portal are skipped. New people are created; the main contact is organization admin." />
        </div>
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            if (selected == null || timeZone === "") return;
            onApprove({
              organizationId: selected.organizationId,
              organizationName: selected.name,
              timeZone,
            });
          }}
        >
          <div className={`${hubModalBodyScrollClassName()} space-y-4 px-4 py-4`}>
            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Submitted organization</h3>
              <p className="text-sm font-medium text-slate-900">{requestedOrg || "None on this request"}</p>
            </section>

            <div className="space-y-1">
              <Label htmlFor="approve-pending-portal-org">Portal organization</Label>
              <SearchableSelect
                id="approve-pending-portal-org"
                value={organizationId}
                disabled={readOnly}
                onValueChange={(option) => setOrganizationId(option?.value ?? "")}
                options={organizations.map((organization) => ({
                  value: String(organization.organizationId),
                  label: organization.name,
                  description: organization.domain,
                }))}
                searchValue={organizationSearch}
                onSearchChange={setOrganizationSearch}
                placeholder="Search and select a portal organization"
                searchPlaceholder="Search by name or domain"
                emptyMessage={
                  organizationsQuery.isError
                    ? organizationsQuery.error instanceof ApiError
                      ? organizationsQuery.error.message
                      : "Could not load portal organizations."
                    : organizationsQuery.isLoading
                      ? "Loading portal organizations..."
                      : "No portal organizations found."
                }
                isLoading={organizationsQuery.isLoading}
                className="mt-1"
              />
              {membershipQuery.isFetching ? (
                <p className="text-xs text-slate-500">Checking emails against the portal...</p>
              ) : null}
              {membershipQuery.isError ? (
                <p className="text-xs text-rose-700">
                  {membershipQuery.error instanceof ApiError
                    ? membershipQuery.error.message
                    : "Could not check users in this organization."}
                </p>
              ) : null}
              {selectedDiffersFromRequested ? (
                <p className="text-xs text-amber-800">
                  Customer typed {requestedOrg}. Assigning to {selected.name}.
                </p>
              ) : null}
              {canSubmit && skippedExistingCount > 0 ? (
                <p className="text-xs text-slate-600">
                  Will add {creatableCount} new {creatableCount === 1 ? "user" : "users"} and skip
                  {" "}{skippedExistingCount} already in the portal.
                </p>
              ) : null}
            </div>

            <div className="space-y-1">
              <Label htmlFor="approve-pending-portal-timezone">Time zone</Label>
              <SearchableSelect
                id="approve-pending-portal-timezone"
                value={timeZone}
                disabled={readOnly}
                onValueChange={(option) => setTimeZone(option?.value ?? "")}
                options={US_TIME_ZONE_OPTIONS.map((option) => ({
                  value: option.value,
                  label: option.label,
                  description: option.value,
                }))}
                searchValue={timeZoneSearch}
                onSearchChange={setTimeZoneSearch}
                placeholder="Search a US time zone"
                searchPlaceholder="Search Eastern, Central, Pacific…"
                emptyMessage="No matching time zones."
                className="mt-1"
              />
            </div>

            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Organization admin</h3>
              <PersonRow
                label="Organization admin"
                firstName={user.adminFirstName}
                lastName={user.adminLastName}
                email={user.adminEmail}
                phone={user.adminPhone}
                role="Org admin"
                issues={people[0]}
              />
            </section>

            {user.additionalUsers.length > 0 ? (
              <section className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Additional users</h3>
                <div className="space-y-2">
                  {user.additionalUsers.map((entry, index) => (
                    <PersonRow
                      key={`${entry.email}-${String(index)}`}
                      label="Additional user"
                      firstName={entry.firstName}
                      lastName={entry.lastName}
                      email={entry.email}
                      phone={entry.phone}
                      role={entry.role}
                      issues={people[index + 1]}
                    />
                  ))}
                </div>
              </section>
            ) : (
              <p className="text-sm text-slate-500">No additional users on this request.</p>
            )}
          </div>
          <div className="flex shrink-0 justify-end gap-2 border-t border-slate-200 px-4 py-3">
            <Button type="button" variant="secondary" disabled={busy} onClick={onClose}>
              {readOnly ? "Close" : "Cancel"}
            </Button>
            {readOnly ? null : (
              <>
                <Button type="button" variant="ghost" disabled={busy} onClick={onDeny}>
                  Deny
                </Button>
                <Button type="submit" disabled={!canSubmit} isLoading={pending}>
                  Approve
                </Button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
